import {
  CalendarTodayOutlinedIcon,
  Card,
  type CardProps,
  CheckCircleOutlineRoundedIcon,
  Divider,
  EllipsisText,
  Flex,
  mergeSx,
  Text,
} from '@shared/components';
import { formatDate } from '@shared/utils';

export interface TaskCardProps extends CardProps {
  type: 'qset-review' | 'question-creation' | 'question-review';
  code: string;
  title: string;
  dateLabel: string;
  dueDate: Date;
  completeTaskCount: number;
  totalTaskCount: number;
  taskCountLabel: string;
  dueStatus?: 'overdue' | 'due-soon';
}

const TASK_TYPE_TITLE = {
  'qset-review': 'QSET REVIEW',
  'question-creation': 'QUESTION CREATION',
  'question-review': 'QUESTION REVIEW',
} as const;

const TASK_TYPE_COLOR = {
  'qset-review': 'success',
  'question-creation': 'info',
  'question-review': 'primary',
} as const;

export default function TaskCard(props: Readonly<TaskCardProps>) {
  const {
    type,
    code,
    title,
    dateLabel,
    dueDate,
    completeTaskCount,
    totalTaskCount,
    taskCountLabel,
    dueStatus,
    sx,
    ...restProps
  } = props;

  return (
    <Card
      elevation={0}
      sx={mergeSx(
        (theme) => ({
          border: `1px solid ${theme.palette.grey[300]}`,
          p: theme.spacing(4),
          pr: theme.spacing(6),
          borderRadius: theme.shape.rounded(2),
        }),
        sx,
      )}
      {...restProps}
    >
      <Flex
        sx={(theme) => ({
          borderLeft: `4px solid ${theme.palette[TASK_TYPE_COLOR[type]].main}`,
          pl: theme.spacing(3),
          gap: theme.spacing(4),
          alignItems: 'center',
        })}
      >
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(2),
            flexGrow: 1,
          })}
        >
          <Flex sx={(theme) => ({ gap: theme.spacing(2), alignItems: 'center' })}>
            {/* REMARK: Task Type Badge */}
            <Flex
              sx={(theme) => ({
                gap: theme.spacing(1),
                alignItems: 'center',
                backgroundColor: theme.palette[TASK_TYPE_COLOR[type]].lighter,
                p: theme.spacing(0.5, 2),
                borderRadius: theme.shape.rounded(0.5),
              })}
            >
              <Text
                sx={{
                  fontWeight: 600,
                  fontSize: '10px',
                  color: (theme) => theme.palette[TASK_TYPE_COLOR[type]].main,
                }}
              >
                {TASK_TYPE_TITLE[type]}
              </Text>
            </Flex>
            <Divider orientation='vertical' variant='middle' flexItem />
            <Text variant='caption' color='text.secondary'>
              {code}
            </Text>
          </Flex>
          <EllipsisText variant='body1Semibold'>{title}</EllipsisText>
        </Flex>
        <Divider orientation='vertical' variant='middle' flexItem />
        <Flex sx={(theme) => ({ gap: theme.spacing(6), alignItems: 'center' })}>
          {/* REMARK: Due Date Section */}
          <Flex
            sx={{
              flexDirection: 'column',
              textAlign: 'right',
            }}
          >
            <Text variant='captionSemibold' color={dueStatus ? 'error.main' : 'text.disabled'}>
              {dateLabel}
            </Text>
            <Flex sx={(theme) => ({ alignItems: 'center', gap: theme.spacing(1) })}>
              <CalendarTodayOutlinedIcon
                sx={{
                  fontSize: '16px',
                  color: dueStatus ? 'error.main' : 'text.secondary',
                }}
              />
              <Text variant='body2Semibold' color={dueStatus ? 'error.main' : 'text.secondary'}>
                {formatDate(dueDate)}
              </Text>
            </Flex>
          </Flex>
          {/* REMARK: Progress Section */}
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              textAlign: 'right',
              gap: theme.spacing(0.5),
            })}
          >
            <Text variant='captionSemibold' color='text.disabled'>
              PROGRESS
            </Text>
            <Flex sx={(theme) => ({ alignItems: 'center', gap: theme.spacing(1) })}>
              <CheckCircleOutlineRoundedIcon sx={{ fontSize: '16px' }} />
              <Text variant='body2Semibold' color='text.secondary'>
                {completeTaskCount}/{totalTaskCount} {taskCountLabel}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
}
